import { useEffect, useRef } from "react";
import {
  initSupabase,
  isConfigured,
  hasCurriculumUnlock,
  useAuthStore,
  SyncEngine,
} from "@music-app/sync-supabase";
import { useCardsStore, usePrefsStore } from "@music-app/persistence";

/**
 * Wire up cloud sync. Call once from the root App component.
 *
 * - Initialises the Supabase client if credentials are present in env.
 * - Subscribes to auth state changes.
 * - When the user is signed in: performs an initial sync, starts Realtime,
 *   subscribes to card-store changes for debounced push, adds a
 *   visibilitychange listener, and sets up a 30 s interval — all cleaned
 *   up when the user signs out or the component unmounts.
 *
 * When VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are absent (a build
 * without sync configured), this hook is a no-op.
 */
export function useSyncWiring(): void {
  const supabaseUrl = import.meta.env["VITE_SUPABASE_URL"] as string | undefined;
  const supabaseKey = import.meta.env["VITE_SUPABASE_ANON_KEY"] as string | undefined;

  // Init Supabase once on mount; idempotent in Strict Mode double-invoke.
  useEffect(() => {
    if (supabaseUrl && supabaseKey) {
      initSupabase(supabaseUrl, supabaseKey);
      useAuthStore.getState().init();
    }
    // Env vars are build-time constants — this intentionally only runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = useAuthStore((s) => s.user);
  const engineRef = useRef<SyncEngine | null>(null);

  // Privileged roles (tester/qa) get the whole curriculum ungated: flip
  // unlockEverything once prefs have hydrated so we don't race the stored value
  // on boot. Only ever set true — never revoke, matching the URL trigger.
  const prefsLoaded = usePrefsStore((s) => s.loaded);
  useEffect(() => {
    if (!prefsLoaded || !hasCurriculumUnlock(user)) return;
    if (usePrefsStore.getState().unlockEverything) return;
    void usePrefsStore.getState().setUnlockEverything(true);
  }, [user, prefsLoaded]);

  useEffect(() => {
    if (!user || !isConfigured()) return;

    const engine = new SyncEngine(user.id);
    engineRef.current = engine;

    // Initial sync on sign-in / app boot.
    void engine.sync();

    // Realtime: any remote card change triggers a pull.
    engine.startRealtime();

    // Debounced push whenever the local card store updates.
    const unsubCards = useCardsStore.subscribe((state, prev) => {
      if (state.cards !== prev.cards) engine.schedulePush();
    });

    // Sync on tab visibility: pull on visible, flush on hide.
    const onVisibility = () => void engine.sync();
    document.addEventListener("visibilitychange", onVisibility);

    // Periodic background sync every 30 s.
    const interval = setInterval(() => void engine.sync(), 30_000);

    return () => {
      engine.destroy();
      unsubCards();
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, [user]);
}
